// Dashboard Macros — répartition énergétique P/G/L, protéines g/kg, repères journaliers.
import { useMemo, useState } from 'react';
import { MacroRings, MacroStackChart } from '@/components/charts';
import {
  ChartCard,
  EmptyState,
  GranularityPicker,
  Kpi,
  PeriodPicker,
  ProgressBar,
} from '@/components/ui';
import {
  aggregateBy,
  consumptionNutrients,
  currentWeight,
  DAILY_REFERENCE,
  filterByPeriod,
  type Granularity,
  macroSplit,
  macroTargetsFromGrams,
  mean,
  type Period,
  PROTEIN_PER_KG_BY_PHASE,
  proteinPerKg,
  resolveTargets,
  round,
  sumNutrients,
  zeroNutrients,
} from '@/domain';
import { useConsumptions, useProfile, useWeights } from '@/hooks/queries';
import { useTheme } from '@/theme/ThemeContext';

export function MacrosPage() {
  const { data: consumptions = [] } = useConsumptions();
  const { data: weights = [] } = useWeights();
  const { data: profile } = useProfile();
  const { palette } = useTheme();
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [period, setPeriod] = useState<Period>('all');

  const targets = profile
    ? resolveTargets(profile.profile, weights.length ? currentWeight(weights) : NaN)
    : { proteinG: 0, carbG: 0, fatG: 0, waterMl: 0 };
  const proteinTarget = targets.proteinG;
  const targetSplit = macroTargetsFromGrams(targets.proteinG, targets.carbG, targets.fatG);

  const scoped = useMemo(
    () => filterByPeriod(consumptions, (c) => c.datetime, period),
    [consumptions, period],
  );

  const model = useMemo(() => {
    if (scoped.length === 0) return null;
    const buckets = aggregateBy(
      scoped,
      (c) => c.datetime,
      granularity,
      (group) => sumNutrients(group.map(consumptionNutrients)),
    );
    const chart = buckets.map((b) => ({
      date: b.start,
      protein: round(b.value.protein),
      carbs: round(b.value.carbs),
      fat: round(b.value.fat),
    }));

    // Moyennes sur les jours réellement saisis.
    const daily = aggregateBy(
      scoped,
      (c) => c.datetime,
      'day',
      (group) => sumNutrients(group.map(consumptionNutrients)),
    ).map((b) => b.value);

    const avg = daily.length
      ? {
          protein: mean(daily.map((n) => n.protein)),
          carbs: mean(daily.map((n) => n.carbs)),
          fat: mean(daily.map((n) => n.fat)),
          saturated: mean(daily.map((n) => n.saturated)),
          sugars: mean(daily.map((n) => n.sugars)),
          fiber: mean(daily.map((n) => n.fiber)),
          salt: mean(daily.map((n) => n.salt)),
        }
      : zeroNutrients();

    const split = macroSplit({
      ...zeroNutrients(),
      protein: avg.protein,
      carbs: avg.carbs,
      fat: avg.fat,
    });
    const bodyWeight = weights.length ? currentWeight(weights) : NaN;
    const protPerKg = proteinPerKg(avg.protein, bodyWeight);

    return { chart, avg, split, protPerKg };
  }, [scoped, weights, granularity]);

  if (!model) {
    return (
      <>
        <div className="page-header">
          <div>
            <h1>Macros</h1>
            <p>Décomposition nutritionnelle.</p>
          </div>
        </div>
        <EmptyState>
          Saisis des consommations (onglet Calories) pour analyser tes macros.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Macros</h1>
          <p>Moyennes journalières et répartition énergétique.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <GranularityPicker value={granularity} onChange={setGranularity} />
        </div>
      </div>

      <div className="grid kpis" style={{ marginBottom: '1rem' }}>
        <Kpi
          label="Protéines / jour"
          value={round(model.avg.protein)}
          unit="g"
          hint={`${model.split.proteinPct}% de l'apport`}
        />
        <Kpi
          label="Glucides / jour"
          value={round(model.avg.carbs)}
          unit="g"
          hint={`${model.split.carbsPct}% de l'apport`}
        />
        <Kpi
          label="Lipides / jour"
          value={round(model.avg.fat)}
          unit="g"
          hint={`${model.split.fatPct}% de l'apport`}
        />
        <Kpi
          label="Protéines / kg"
          value={Number.isFinite(model.protPerKg) ? round(model.protPerKg, 2) : '—'}
          unit="g/kg"
          hint={`repère sèche : ${PROTEIN_PER_KG_BY_PHASE.cut.min}–${PROTEIN_PER_KG_BY_PHASE.cut.max} (ISSN 2017)`}
        />
      </div>

      <div className="grid charts">
        <ChartCard
          title="Macronutriments empilés"
          subtitle="grammes par période (protéines / glucides / lipides)"
        >
          <MacroStackChart data={model.chart} palette={palette} />
        </ChartCard>

        <ChartCard
          title="Répartition réelle"
          subtitle={`cible : ${targets.proteinG} g P · ${targets.carbG} g G · ${targets.fatG} g L (${targetSplit.proteinPct}/${targetSplit.carbPct}/${targetSplit.fatPct} %)`}
        >
          <MacroRings
            actual={{ protein: model.avg.protein, carbs: model.avg.carbs, fat: model.avg.fat }}
            palette={palette}
          />
        </ChartCard>

        <div className="card">
          <h3>Objectifs &amp; repères (moyenne/jour)</h3>
          <div style={{ marginTop: '0.75rem' }}>
            {proteinTarget > 0 && (
              <RefLine
                label="Protéines (objectif)"
                value={model.avg.protein}
                reference={round(proteinTarget)}
                unit="g"
                palette={palette}
              />
            )}
            {targets.carbG > 0 && (
              <RefLine
                label="Glucides (objectif)"
                value={model.avg.carbs}
                reference={targets.carbG}
                unit="g"
                palette={palette}
              />
            )}
            {targets.fatG > 0 && (
              <RefLine
                label="Lipides (objectif)"
                value={model.avg.fat}
                reference={targets.fatG}
                unit="g"
                palette={palette}
              />
            )}
            <RefLine
              label="Acides gras saturés"
              value={model.avg.saturated}
              reference={DAILY_REFERENCE.saturatedG}
              unit="g"
              invert
              source="≤ 10 %E (OMS)"
              palette={palette}
            />
            <RefLine
              label="Sucres (totaux mesurés)"
              value={model.avg.sugars}
              reference={DAILY_REFERENCE.sugarsG}
              unit="g"
              invert
              // Les données sont des sucres TOTAUX ; l'OMS plafonne les sucres LIBRES.
              // Le repère 50 g (< 10 %E) est donc une borne prudente : le libellé le dit
              // explicitement pour ne pas sur-alerter à tort (fruits/laitages). Cf. Annexes.
              source="borne prudente : < 10 %E de sucres libres (OMS) — vos données incluent les sucres naturels"
              palette={palette}
            />
            <RefLine
              label="Sel"
              value={model.avg.salt}
              reference={DAILY_REFERENCE.saltG}
              unit="g"
              invert
              source="< 5 g/j (OMS)"
              palette={palette}
            />
            <RefLine
              label="Fibres"
              value={model.avg.fiber}
              reference={DAILY_REFERENCE.fiberG}
              unit="g"
              source="~30 g/j (ANSES)"
              palette={palette}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// invert : dépasser le repère est mauvais (AGS, sucres, sel) ; sinon l'atteindre est bon (fibres).
function RefLine({
  label,
  value,
  reference,
  unit,
  invert,
  source,
  palette,
}: {
  label: string;
  value: number;
  reference: number;
  unit: string;
  invert?: boolean;
  source?: string;
  palette: { good: string; critical: string };
}) {
  const pct = round((value / reference) * 100, 0);
  const over = value > reference;
  const color = invert
    ? over
      ? palette.critical
      : palette.good
    : over
      ? palette.good
      : palette.critical;
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</span>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {round(value, 1)} / {reference} {unit}
        </span>
      </div>
      <ProgressBar pct={Math.min(100, pct)} color={color} />
      {source && (
        <span className="muted" style={{ fontSize: '0.72rem' }}>
          repère : {source}
        </span>
      )}
    </div>
  );
}
